import { CombinedGraphQLErrors } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { handleApolloResult } from "@repo/apollo-utils";
import { useRefreshWorkspaceAppearance } from "@repo/workspace-mark";
import * as Sentry from "@sentry/nextjs";
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useAsyncFn } from "react-use";
import { graphql, type ResultOf } from "../../../gql/graphql";
import { WorkspaceGlobalsFragment } from "../../workspaceGlobals";

export type AuthState =
  | {
      status:
        | "workspace_forbidden"
        | "current_shop_not_set"
        | "service_inactive"
        | "ip_restricted";
      user: null;
    }
  | {
      status: "ok";
      user: User;
    }
  | {
      status: "unauthenticated"; // 認証なし
      user: null;
    }
  | {
      status: "unauthorized"; // 権限なし
      user: null;
    }
  | {
      status: "error"; // その他のエラー
      user: null;
    }
  | {
      status: "unknown";
      user: null;
    };

type ContextValue = {
  authState: AuthState;
  isLoading: boolean;
  check: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Arc トークン端点 / Arc GraphQL / karte-agent が IP 制限で拒否したときに、
   * アプリ全体を ip_restricted 状態へ倒す。IP 制限は Workspace 単位なので Rails 側も同じ判定になる。
   */
  markIpRestricted: () => void;
};

export const AuthContext = createContext<ContextValue>({
  authState: {
    status: "unknown",
    user: null,
  },
  isLoading: false,
  check: () => {
    throw new Error("Not implemented");
  },
  refresh: () => {
    throw new Error("Not implemented");
  },
  markIpRestricted: () => {},
});

const CurrentWorkspaceFragment = graphql(
  `
  fragment CurrentWorkspaceFragment on Workspace @_unmask {
    id
    name
    displayName
    mark { label emoji color }
    ...WorkspaceGlobals
  }
  `,
  [WorkspaceGlobalsFragment],
);

const UserFragment = graphql(
  `
  fragment UserFragment on User @_unmask {
    showWorkspaceNameInTitle
    id
    email
    enableBetaFeatures
    workspaces {
      id
      name
    }
    currentWorkspace {
      ...CurrentWorkspaceFragment
    }
    appMap {
      items {
        id
        status
      }
    }
  }
`,
  [CurrentWorkspaceFragment],
);

export type User = ResultOf<typeof UserFragment>;
export type CurrentWorkspace = ResultOf<typeof CurrentWorkspaceFragment>;

const UserQuery = graphql(
  `
    query AuthProvider_User {
      currentUser {
        ...UserFragment
      }
    }
  `,
  [UserFragment],
);

export const AuthProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    status: "unknown",
    user: null,
  });
  const apolloClient = useApolloClient();
  const authGeneration = useRef(0);

  const fetchUser = useCallback(async () => {
    const generation = ++authGeneration.current;
    try {
      const result = await handleApolloResult(
        apolloClient.query({
          query: UserQuery,
          fetchPolicy: "network-only",
          errorPolicy: "all",
        }),
      );
      if (generation !== authGeneration.current) {
        return;
      }
      if (result.error) {
        if (CombinedGraphQLErrors.is(result.error)) {
          const workspaceErrorStatuses = {
            IP_RESTRICTED: "ip_restricted",
            WORKSPACE_FORBIDDEN: "workspace_forbidden",
            CURRENT_SHOP_NOT_SET: "current_shop_not_set",
            SERVICE_INACTIVE: "service_inactive",
          } as const;
          for (const [code, status] of Object.entries(workspaceErrorStatuses)) {
            if (
              result.error.errors.some(
                (error) => error.extensions?.code === code,
              )
            ) {
              setAuthState({ status, user: null });
              return;
            }
          }
          if (
            result.error.errors.some(
              (error) => error.extensions?.code === "UNAUTHENTICATED",
            )
          ) {
            setAuthState({
              status: "unauthenticated",
              user: null,
            });
            return;
          }
          if (
            result.error.errors.some(
              (error) => error.extensions?.code === "UNAUTHORIZED",
            )
          ) {
            setAuthState({
              status: "unauthorized",
              user: null,
            });
            return;
          }
        }

        setAuthState({
          status: "error",
          user: null,
        });
        return;
      }

      const user = result.data?.currentUser;
      if (!user) {
        setAuthState({
          status: "unauthenticated",
          user: null,
        });
        return;
      }

      setAuthState({
        status: "ok",
        user,
      });
    } catch (error) {
      if (generation !== authGeneration.current) {
        return;
      }
      Sentry.captureException(error);
      setAuthState({
        status: "error",
        user: null,
      });
    }
  }, [apolloClient]);

  const refreshWorkspaceAppearance = useCallback(async () => {
    const { data } = await apolloClient.query({
      query: UserQuery,
      // 保存後の再取得に合流せず、古い応答を共有キャッシュにも戻さない。
      fetchPolicy: "no-cache",
      context: { queryDeduplication: false },
    });
    const user = data?.currentUser;
    if (!user) return;
    setAuthState((previous) =>
      previous === authState &&
      previous.status === "ok" &&
      previous.user?.id === user.id
        ? { status: "ok", user }
        : previous,
    );
  }, [apolloClient, authState]);
  useRefreshWorkspaceAppearance(
    refreshWorkspaceAppearance,
    authState.status === "ok",
  );

  const check = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const refresh = useCallback(async () => {
    if (authState.status !== "ok") {
      // 認証されていない場合は、何もしない
      return;
    }

    await fetchUser();
  }, [authState, fetchUser]);

  const [doCheckState, doCheck] = useAsyncFn(check, [check]);

  const isLoading = doCheckState.loading;

  const markIpRestricted = useCallback(() => {
    authGeneration.current += 1;
    setAuthState({ status: "ip_restricted", user: null });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authState,
        isLoading,
        check: doCheck,
        refresh,
        markIpRestricted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
